import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import ComputerIcon from '@mui/icons-material/Computer';
import GapIcon from '@mui/icons-material/ReportProblem';
import RecommendIcon from '@mui/icons-material/ThumbUp';
import ArchitectureIcon from '@mui/icons-material/AccountTree';
import { getTechnologyAssessment, saveTechnologyAssessment, fetchTechnologyAssessmentFromSOW } from '../../utils/discoveryPhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const TechnologyAssessmentPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [assessment, setAssessment] = useState({
    overview: '',
    currentTechnologyStack: [],
    technologyGaps: [],
    recommendedTechnologies: [],
    architectureRecommendations: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState({
    overview: '',
    currentTechnologyStack: [],
    technologyGaps: [],
    recommendedTechnologies: [],
    architectureRecommendations: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({});
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load technology assessment data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadTechnologyAssessmentFromDB();
    }
  }, [projectId]);
  
  // Load technology assessment from database
  const loadTechnologyAssessmentFromDB = async () => {
    setLoading(true);
    try {
      const assessmentData = await getTechnologyAssessment(projectId);
      if (assessmentData) {
        setAssessment(assessmentData);
        setEditingAssessment(assessmentData);
      }
    } catch (error) {
      console.error('Error loading technology assessment:', error);
      showSnackbar('Error loading technology assessment data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Save technology assessment to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveTechnologyAssessment(projectId, editingAssessment);
      setAssessment(editingAssessment);
      setEditMode(false);
      showSnackbar('Technology assessment saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving technology assessment:', error);
      showSnackbar('Error saving technology assessment', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch technology assessment from project documents using AI
  const handleFetchFromSOW = async () => {
    setAiLoading(true);
    try {
      const response = await fetchTechnologyAssessmentFromSOW(projectId);
      if (response.data) {
        setEditingAssessment(response.data);
        setAssessment(response.data);
        showSnackbar('Technology assessment generated from project documents successfully!', 'success');
      }
    } catch (error) {
      console.error('Error fetching technology assessment from project documents:', error);
      showSnackbar('Error generating technology assessment from project documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Dialog functions
  const handleOpenDialog = (type, item = null, index = null) => {
    setDialogType(type);
    
    if (type === 'currentTechnologyStack') {
      setDialogItem(item || { 
        name: '', 
        version: '', 
        category: '', 
        purpose: '', 
        status: 'Active', 
        vendor: '', 
        licenseType: '', 
        supportLevel: '' 
      });
    } else if (type === 'technologyGaps') {
      setDialogItem(item || { 
        gap: '', 
        description: '', 
        impact: '', 
        priority: 'Medium', 
        category: '', 
        currentState: '', 
        targetState: '' 
      });
    } else if (type === 'recommendedTechnologies') {
      setDialogItem(item || { 
        technology: '', 
        category: '', 
        purpose: '', 
        benefits: [], 
        risks: [], 
        implementation: '', 
        cost: '', 
        timeline: '' 
      });
    } else if (type === 'architectureRecommendations') {
      setDialogItem(item || { 
        recommendation: '', 
        description: '', 
        rationale: '', 
        impact: '', 
        priority: 'Medium', 
        effort: '', 
        dependencies: [] 
      });
    }
    
    setDialogItemIndex(index);
    setOpenDialog(true);
  };
  
  const handleSaveDialog = () => {
    const newData = { ...editingAssessment };
    
    if (dialogItemIndex !== null) {
      // Edit existing item
      newData[dialogType][dialogItemIndex] = dialogItem;
    } else {
      // Add new item
      newData[dialogType].push(dialogItem);
    }
    
    setEditingAssessment(newData);
    setOpenDialog(false);
  };
  
  const handleDeleteItem = (type, index) => {
    const newData = { ...editingAssessment };
    newData[type].splice(index, 1);
    setEditingAssessment(newData);
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const renderTechnologyStackCard = (tech, index) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'Active': return 'success';
        case 'Deprecated': return 'error';
        case 'Planned': return 'info';
        case 'Evaluation': return 'warning';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {tech.name}
            </Typography>
            <Chip 
              label={tech.status} 
              color={getStatusColor(tech.status)}
              size="small"
            />
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Version:</strong> {tech.version}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {tech.category}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Vendor:</strong> {tech.vendor}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>License:</strong> {tech.licenseType}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Purpose:</strong> {tech.purpose}
              </Typography>
            </Grid>
            {tech.supportLevel && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Support Level:</strong> {tech.supportLevel}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('currentTechnologyStack', tech, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('currentTechnologyStack', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderGapCard = (gap, index) => {
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {gap.gap}
            </Typography>
            <Chip 
              label={gap.priority} 
              color={getPriorityColor(gap.priority)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {gap.description}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {gap.category}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Impact:</strong> {gap.impact}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Current State:</strong> {gap.currentState}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Target State:</strong> {gap.targetState}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('technologyGaps', gap, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('technologyGaps', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderRecommendationCard = (rec, index) => {
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {rec.technology}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {rec.category}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Timeline:</strong> {rec.timeline}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Purpose:</strong> {rec.purpose}
              </Typography>
            </Grid>
            {rec.cost && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Cost:</strong> {rec.cost}
                </Typography>
              </Grid>
            )}
          </Grid>
          
          {rec.benefits && rec.benefits.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Benefits:</strong>
              </Typography>
              <List dense>
                {rec.benefits.map((benefit, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${benefit}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {rec.risks && rec.risks.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Risks:</strong>
              </Typography>
              <List dense>
                {rec.risks.map((risk, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${risk}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {rec.implementation && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Implementation:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rec.implementation}
              </Typography>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('recommendedTechnologies', rec, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('recommendedTechnologies', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderArchitectureCard = (arch, index) => {
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {arch.recommendation}
            </Typography>
            <Chip 
              label={arch.priority} 
              color={getPriorityColor(arch.priority)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {arch.description}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Impact:</strong> {arch.impact}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Effort:</strong> {arch.effort}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Rationale:</strong> {arch.rationale}
              </Typography>
            </Grid>
          </Grid>
          
          {arch.dependencies && arch.dependencies.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Dependencies:</strong>
              </Typography>
              <List dense>
                {arch.dependencies.map((dep, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${dep}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('architectureRecommendations', arch, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('architectureRecommendations', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderSection = (type, title, items, renderCard, icon) => {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            {icon}
            <Typography variant="h6" sx={{ ml: 1 }}>{title}</Typography>
            <Chip 
              label={items.length} 
              size="small" 
              sx={{ ml: 2 }} 
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog(type)}
                sx={{ mb: 2 }}
              >
                Add {title.slice(0, -1)}
              </Button>
            )}
            {items.map((item, index) => renderCard(item, index))}
            {items.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No {title.toLowerCase()} defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  if (loading && !assessment.overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <BuildIcon />
            Technology Assessment
          </SectionTitle>
          <Box>
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleFetchFromSOW}
                  disabled={aiLoading}
                  sx={{ mr: 1, backgroundColor: '#F5821F' }}
                >
                  {aiLoading ? <CircularProgress size={20} /> : 'Fetch From Project Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    setEditingAssessment(assessment);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Evaluate current technology stack and provide recommendations for {project?.name}.
        </Typography>
      </Paper>
      
      {/* Overview Section */}
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Technology Assessment Overview"
              value={editingAssessment.overview}
              onChange={(e) => setEditingAssessment({
                ...editingAssessment,
                overview: e.target.value
              })}
            />
          ) : (
            <Typography>
              {assessment.overview || 'No overview available. Click "Edit" to add content or "Fetch From Project Documents" to generate from your project documents.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Current Technology Stack */}
      {renderSection('currentTechnologyStack', 'Current Technology Stack', editingAssessment.currentTechnologyStack, renderTechnologyStackCard, <ComputerIcon />)}
      
      {/* Technology Gaps */}
      {renderSection('technologyGaps', 'Technology Gaps', editingAssessment.technologyGaps, renderGapCard, <GapIcon />)}
      
      {/* Recommended Technologies */}
      {renderSection('recommendedTechnologies', 'Recommended Technologies', editingAssessment.recommendedTechnologies, renderRecommendationCard, <RecommendIcon />)}
      
      {/* Architecture Recommendations */}
      {renderSection('architectureRecommendations', 'Architecture Recommendations', editingAssessment.architectureRecommendations, renderArchitectureCard, <ArchitectureIcon />)}
      
      {/* Dialog for adding/editing items */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex !== null ? 'Edit' : 'Add'} {
            dialogType === 'currentTechnologyStack' ? 'Technology Stack Item' :
            dialogType === 'technologyGaps' ? 'Technology Gap' :
            dialogType === 'recommendedTechnologies' ? 'Recommended Technology' :
            'Architecture Recommendation'
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {dialogType === 'currentTechnologyStack' && (
              <>
                <TextField
                  fullWidth
                  label="Technology Name"
                  value={dialogItem.name || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Version"
                  value={dialogItem.version || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, version: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Category"
                  value={dialogItem.category || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Purpose"
                  value={dialogItem.purpose || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, purpose: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={dialogItem.status || 'Active'}
                  onChange={(e) => setDialogItem({ ...dialogItem, status: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="Active">Active</option>
                  <option value="Deprecated">Deprecated</option>
                  <option value="Planned">Planned</option>
                  <option value="Evaluation">Evaluation</option>
                </TextField>
                <TextField
                  fullWidth
                  label="Vendor"
                  value={dialogItem.vendor || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, vendor: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="License Type"
                  value={dialogItem.licenseType || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, licenseType: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Support Level"
                  value={dialogItem.supportLevel || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, supportLevel: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'technologyGaps' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Gap"
                  value={dialogItem.gap || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, gap: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Impact"
                  value={dialogItem.impact || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, impact: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  value={dialogItem.priority || 'Medium'}
                  onChange={(e) => setDialogItem({ ...dialogItem, priority: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </TextField>
                <TextField
                  fullWidth
                  label="Category"
                  value={dialogItem.category || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Current State"
                  value={dialogItem.currentState || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, currentState: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Target State"
                  value={dialogItem.targetState || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, targetState: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'recommendedTechnologies' && (
              <>
                <TextField
                  fullWidth
                  label="Technology"
                  value={dialogItem.technology || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, technology: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Category"
                  value={dialogItem.category || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Purpose"
                  value={dialogItem.purpose || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, purpose: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Implementation"
                  value={dialogItem.implementation || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, implementation: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Cost"
                  value={dialogItem.cost || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, cost: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Timeline"
                  value={dialogItem.timeline || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, timeline: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'architectureRecommendations' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Recommendation"
                  value={dialogItem.recommendation || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, recommendation: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rationale"
                  value={dialogItem.rationale || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, rationale: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Impact"
                  value={dialogItem.impact || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, impact: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  value={dialogItem.priority || 'Medium'}
                  onChange={(e) => setDialogItem({ ...dialogItem, priority: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </TextField>
                <TextField
                  fullWidth
                  label="Effort"
                  value={dialogItem.effort || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, effort: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            {dialogItemIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechnologyAssessmentPage;